import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, updateData, deleteData } from "../../services/request"
import { countries } from "../../data"

const infoRow = (label, value) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminHospitalDetails = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [hospital, setHospital] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")

  // Edit form state
  const [editing, setEditing] = useState(false)
  const [organization, setOrganization] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [country, setCountry] = useState("")
  const [address, setAddress] = useState("")
  const [loadingCountries] = useState(false)

  // Action states
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fetchHospital = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/users`
    const params = { userId }
    const res = await getData(url, {}, params)
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        const data = res.result.data?.data
        const record = Array.isArray(data) ? data[0] : data
        setHospital(record || null)
        if(record){
          setOrganization(record.organization || "")
          setEmail(record.email || "")
          setPhoneNumber(record.phoneNumber || "")
          setCountry(record.country || "")
          setAddress(record.address || "")
        }
        if(!record){
          setError('Hospital not found')
        }
      }else{
        setError(res.result.data?.message || 'Hospital not found')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchHospital()
  }, [userId])

  const handleEdit = () => {
    setEditing(true)
    setError("")
    setGenMsg("")
  }

  const handleCancelEdit = () => {
    setEditing(false)
    if (hospital) {
      setOrganization(hospital.organization || "")
      setEmail(hospital.email || "")
      setPhoneNumber(hospital.phoneNumber || "")
      setCountry(hospital.country || "")
      setAddress(hospital.address || "")
    }
  }

  const handleUpdate = async () => {
    setError("")
    setGenMsg("")
    if (!organization || organization.trim().length < 2) {
      setError("Organization name must be at least 2 characters")
      return
    }
    if (!email || !email.includes('@')) {
      setError("Valid email is required")
      return
    }
    if (!phoneNumber || phoneNumber.length < 10) {
      setError("Valid phone number is required")
      return
    }
    if (!country) {
      setError("Country is required")
      return
    }
    if (!address || address.trim().length < 5) {
      setError("Address must be at least 5 characters")
      return
    }

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/users/update-account`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('userId', userId)
    params.append('organization', organization.trim())
    params.append('email', email.trim().toLowerCase())
    params.append('phoneNumber', phoneNumber.trim())
    params.append('country', country)
    params.append('address', address.trim())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg("Hospital updated successfully")
        setEditing(false)
        fetchHospital()
      } else {
        setError(res.result.data?.message || 'Could not update hospital')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleToggleStatus = async () => {
    setError("")
    setGenMsg("")
    const newStatus = hospital.status === 1 ? 0 : 1

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/users/status`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('userId', userId)
    params.append('status', newStatus.toString())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg(`Hospital ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`)
        fetchHospital()
      } else {
        setError(res.result.data?.message || 'Could not update status')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete hospital "${hospital?.organization}"? This action cannot be undone.`)) {
      return
    }

    setError("")
    setGenMsg("")
    setDeleting(true)
    const url = `${getAPIBaseURL()}/v1/users`
    const res = await deleteData(url, {}, { userId })
    setDeleting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        navigate("/admin/app/hospitals")
      } else {
        setError(res.result.data?.message || 'Could not delete hospital')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <Link to="/admin/app/hospitals" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
        ← Back to hospitals
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h1 className="text-2xl font-bold text-gray-900">Hospital Details</h1>
          <p className="mt-1 text-sm text-gray-600">User ID: {userId}</p>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error && !hospital ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
          ) : !hospital ? (
            <div className="text-sm text-gray-500">Hospital not found.</div>
          ) : (
            <>
              {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
              ) : null}
              {genMsg ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
              ) : null}

              {editing ? (
                <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Edit Hospital Details</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-900">Organization Name *</label>
                      <input
                        type="text"
                        value={organization}
                        onChange={(e) => setOrganization(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Phone Number *</label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Country *</label>
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="">{loadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                        {countries.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-900">Address *</label>
                      <textarea
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        rows={3}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleUpdate}
                      disabled={submitting}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={submitting}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-100">
                  {infoRow('Organization', hospital.organization)}
                  {infoRow('Email', hospital.email)}
                  {infoRow('Phone Number', hospital.phoneNumber)}
                  {infoRow('Address', hospital.address)}
                  {infoRow('Country', hospital.country)}
                  {infoRow('Status', (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      hospital.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {hospital.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  ))}
                  {infoRow('Created At', formatDate(hospital.createdAt))}
                  {infoRow('Updated At', formatDate(hospital.updatedAt))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                {!editing && (
                  <button
                    onClick={handleEdit}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Edit Details
                  </button>
                )}
                <button
                  onClick={handleToggleStatus}
                  disabled={submitting}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${
                    hospital.status === 1
                      ? 'border border-red-200 bg-white text-red-600 hover:bg-red-50'
                      : 'border border-green-200 bg-white text-green-600 hover:bg-green-50'
                  }`}
                >
                  {submitting ? 'Updating...' : hospital.status === 1 ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {deleting ? 'Deleting...' : 'Delete Hospital'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminHospitalDetails
